/**
 * utils/api.js
 * Central API client with automatic token refresh
 * All modules import { api } from '../utils/api'
 */

const BASE = import.meta.env.VITE_API_URL || '';

async function refreshToken() {
  const rt = localStorage.getItem('hyring_refresh_token');
  if (!rt) return null;
  try {
    const r = await fetch(`${BASE}/api/auth/refresh`, {
      method:'POST', headers:{'Content-Type':'application/json'},
      body: JSON.stringify({ refresh_token: rt })
    });
    if (!r.ok) return null;
    const d = await r.json();
    localStorage.setItem('hyring_access_token', d.access_token);
    localStorage.setItem('hyring_refresh_token', d.refresh_token);
    return d.access_token;
  } catch { return null; }
}

async function request(path, options = {}) {
  const token = localStorage.getItem('hyring_access_token');
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  let res = await fetch(`${BASE}${path}`, { ...options, headers });

  // Auto-refresh on 401
  if (res.status === 401) {
    const newToken = await refreshToken();
    if (newToken) {
      headers['Authorization'] = `Bearer ${newToken}`;
      res = await fetch(`${BASE}${path}`, { ...options, headers });
    } else {
      // Force logout
      localStorage.clear();
      window.location.reload();
      return;
    }
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

export const api = {
  get:    (path)         => request(path),
  post:   (path, body)   => request(path, { method:'POST',   body: JSON.stringify(body) }),
  patch:  (path, body)   => request(path, { method:'PATCH',  body: JSON.stringify(body) }),
  delete: (path)         => request(path, { method:'DELETE' }),

  // File upload (multipart)
  upload: (path, formData) => {
    const token = localStorage.getItem('hyring_access_token');
    return fetch(`${BASE}${path}`, {
      method:'POST',
      headers: token ? { Authorization:`Bearer ${token}` } : {},
      body: formData,
    }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(new Error(e.error))));
  },
};

// ── Shared UI components used across all modules ──────────────

export function Spinner() {
  return (
    <div className="thinking">
      <div className="thinking-dot"/><div className="thinking-dot"/><div className="thinking-dot"/>
    </div>
  );
}

export function EmptyState({ icon = '◌', message }) {
  return (
    <div style={{textAlign:'center', padding:'40px 20px', color:'var(--mu)', fontFamily:'var(--mono)', fontSize:12}}>
      <div style={{fontSize:32, marginBottom:12, opacity:0.3}}>{icon}</div>
      {message}
    </div>
  );
}

export function ScoreColor(score, max=5) {
  const pct = score / max;
  return pct >= 0.85 ? '#4ade80' : pct >= 0.7 ? 'var(--gd)' : pct >= 0.5 ? 'var(--or)' : '#f87171';
}

export function Stars({ score }) {
  const full = Math.floor(score);
  const half = (score - full) >= 0.4;
  const clip = `polygon(50% 0%,61% 35%,98% 35%,68% 57%,79% 91%,50% 70%,21% 91%,32% 57%,2% 35%,39% 35%)`;
  return (
    <div style={{display:'flex', gap:3, alignItems:'center'}}>
      <div style={{display:'flex', gap:2}}>
        {[1,2,3,4,5].map(i => (
          <div key={i} style={{width:13, height:13, clipPath:clip,
            background: i<=full ? 'var(--gd)' : (i===full+1&&half ? 'linear-gradient(90deg,var(--gd) 50%,rgba(255,255,255,0.1) 50%)' : 'rgba(255,255,255,0.1)')}}/>
        ))}
      </div>
      <span style={{fontFamily:'var(--mono)', fontSize:13, fontWeight:500, color:'var(--gd)'}}>{score.toFixed(1)}</span>
    </div>
  );
}

export function RecommendBadge({ rec }) {
  const map = {
    strong_yes: ['gr','Strong yes'],
    yes:        ['gr','Yes'],
    maybe:      ['gd','Maybe'],
    no:         ['rd','No'],
    strong_no:  ['rd','Strong no'],
  };
  const [cls, label] = map[rec] || ['mu', rec || '—'];
  return <span className={`badge ${cls}`}>{label}</span>;
}
